import { useContext } from "react";
import { MdOutlineEdit } from "react-icons/md";
import { useIntl } from "react-intl";
import { useMap } from "react-map-gl/maplibre";

import {
  FileIcon,
  FolderIcon,
  LocationIcon,
  MenuAction,
  MoveDirectoryIcon,
  MoveMapIcon,
  TenantContext,
  TrashIcon,
} from "@nens/ranalib";
import { combineUrlAndParams } from "@nens/shared-utils";

import { TAB_KEY } from "@/features/project";
import { ProjectContext } from "@/stores/project-provider";
import { convertFileIdToQueryParams } from "@/utils/convert-file-id-to-query-params";

import {
  getFileByPath,
  getFolders,
  getIsEditable,
  getIsPublic,
  getMaps,
  getOpenMap,
  getSelectedLayers,
} from "../../../store/selectors";
import { usePublicationStore } from "../../../store/store";
import { Folder, Layer } from "../../../types/publication";
import { useLayerBounds } from "../../map-layers/hooks/use-layer-bounds";
import { useMoveActions } from "../hooks/use-move-actions";

export function useLayerListItemActions(
  layer: Layer,
  editName: () => void,
  removeLayer: (layer: Layer) => void,
  isFirst: boolean,
  isLast: boolean,
  folder?: Folder
): MenuAction[] {
  const intl = useIntl();
  const map = useMap().current;
  const { tenant } = useContext(TenantContext);
  const isPublic = usePublicationStore(getIsPublic);
  const isEditable = usePublicationStore(getIsEditable);
  const maps = usePublicationStore(getMaps);
  const openMap = usePublicationStore(getOpenMap);
  const { bounds, boundsLoading } = useLayerBounds(layer);
  const selectedLayers = usePublicationStore(getSelectedLayers);

  const removeLayerFromFolder = usePublicationStore((state) => state.removeLayerFromFolder);
  const addLayerToFolder = usePublicationStore((state) => state.addLayerToFolder);
  const moveLayersToPosition = usePublicationStore((state) => state.moveLayersToPosition);
  const addLayersToFolder = usePublicationStore((state) => state.addLayersToFolder);
  const moveLayersToMap = usePublicationStore((state) => state.moveLayersToMap);
  const file = usePublicationStore(getFileByPath(layer.filePath));
  const projectContext = useContext(ProjectContext);

  const folders = usePublicationStore(getFolders);

  const moveActions = useMoveActions({
    onMove: (dir) => moveLayersToPosition([layer.id], dir, folder?.id),
    isFirst,
    isLast,
  });

  // Get the IDs of all selected layers
  const selectedLayerIds = selectedLayers.map((l) => l.id);
  const hasMultipleSelected = selectedLayerIds.length > 1;

  const actionsForAllUsers = [
    {
      label: intl.formatMessage({
        id: "publication.layer.contextmenu.zoom_to_layer",
        defaultMessage: "Zoom to layer",
      }),
      icon: <LocationIcon />,
      action: () => {
        if (map && bounds) {
          map.fitBounds([
            [bounds[0], bounds[1]],
            [bounds[2], bounds[3]],
          ]);
        }
      },
      disabledText:
        !boundsLoading && bounds
          ? undefined
          : intl.formatMessage({
              id: "publication.layer.contextmenu.zoom_to_layer.no_bounds",
              defaultMessage: "Zoom to layer (bounds not available)",
            }),
    },
  ];

  if (isPublic) {
    return actionsForAllUsers;
  }

  const viewFileActions: MenuAction[] = [];
  if (file) {
    const projectSlug = projectContext.slug;
    const fileId = file.file.id;

    const url = `${combineUrlAndParams(
      `${window.location.origin}/${tenant}/projects/${projectSlug}`,
      {
        [TAB_KEY]: 1,
      }
    )}&${convertFileIdToQueryParams(fileId, file.ref)}`;

    viewFileActions.push({
      icon: <FileIcon />,
      label: intl.formatMessage({
        id: "publication.layer.contextmenu.view_file",
        defaultMessage: "View file",
      }),
      to: url,
    });
  }

  if (!isEditable) {
    return [...actionsForAllUsers, ...viewFileActions];
  }

  if (hasMultipleSelected) {
    const bulkMoveActions: MenuAction[] = [];

    // Move up/down only if not all items are at the start/end
    if (!isFirst) {
      bulkMoveActions.push({
        label: intl.formatMessage({
          id: "publication.layer.contextmenu.move_up",
          defaultMessage: "Move up",
        }),
        action: () => moveLayersToPosition(selectedLayerIds, "up"),
      });
    }

    if (!isLast) {
      bulkMoveActions.push({
        label: intl.formatMessage({
          id: "publication.layer.contextmenu.move_down",
          defaultMessage: "Move down",
        }),
        action: () => moveLayersToPosition(selectedLayerIds, "down"),
      });
    }

    bulkMoveActions.push({
      label: intl.formatMessage({
        id: "publication.layer.contextmenu.move_to_top",
        defaultMessage: "Move to top",
      }),
      action: () => moveLayersToPosition(selectedLayerIds, "top"),
    });

    bulkMoveActions.push({
      label: intl.formatMessage({
        id: "publication.layer.contextmenu.move_to_bottom",
        defaultMessage: "Move to bottom",
      }),
      action: () => moveLayersToPosition(selectedLayerIds, "bottom"),
    });

    const bulkActions: MenuAction[] = [
      {
        label: intl.formatMessage({
          id: "publication.layer.contextmenu.move_all",
          defaultMessage: "Move all",
        }),
        icon: <MoveDirectoryIcon />,
        subActions: bulkMoveActions,
      },
    ];

    if (folders.length > 0 && !folder) {
      bulkActions.push({
        label: intl.formatMessage({
          id: "publication.layer.contextmenu.add_all_to_folder",
          defaultMessage: "Add all to folder",
        }),
        icon: <MoveDirectoryIcon />,
        subActions: folders.map((folder) => ({
          label: folder.name,
          icon: <MoveDirectoryIcon />,
          action: () => addLayersToFolder([folder.id], selectedLayerIds),
        })),
      });
    }

    if (maps.length > 1) {
      bulkActions.push({
        label: intl.formatMessage({
          id: "publication.layer.contextmenu.move_all_to_map",
          defaultMessage: "Move all to map",
        }),
        icon: <MoveMapIcon />,
        subActions: maps
          .filter((map) => map.id !== openMap.id)
          .map((map) => ({
            label: map.name,
            icon: <MoveMapIcon />,
            action: () => moveLayersToMap(selectedLayerIds, map.id),
          })),
      });
    }

    return bulkActions;
  }

  const actions: MenuAction[] = [
    ...actionsForAllUsers,
    {
      label: intl.formatMessage({
        id: "publication.layer.contextmenu.rename_layer",
        defaultMessage: "Rename layer",
      }),
      icon: <MdOutlineEdit />,
      action: () => editName(),
    },
    {
      label: intl.formatMessage({
        id: "publication.layer.contextmenu.remove_layer",
        defaultMessage: "Remove layer",
      }),
      icon: <TrashIcon />,
      action: () => removeLayer(layer),
    },
    moveActions,
    ...viewFileActions,
  ];

  if (folder) {
    actions.push({
      label: intl.formatMessage({
        id: "publication.layer.contextmenu.remove_from_folder",
        defaultMessage: "Remove from folder",
      }),
      icon: <FolderIcon />,
      action: () => removeLayerFromFolder(folder, layer),
    });
  } else if (folders.length > 0) {
    actions.push({
      label: intl.formatMessage({
        id: "publication.layer.contextmenu.add_to_folder",
        defaultMessage: "Add to folder",
      }),
      icon: <MoveDirectoryIcon />,
      subActions: folders.map((folder) => ({
        label: folder.name,
        icon: <MoveDirectoryIcon />,
        action: () => addLayerToFolder(folder, layer),
      })),
    });
  }

  if (maps.length > 1) {
    actions.push({
      label: intl.formatMessage({
        id: "publication.layer.contextmenu.move_to_map",
        defaultMessage: "Move to map",
      }),
      icon: <MoveMapIcon />,
      subActions: maps
        .filter((map) => map.id !== openMap.id)
        .map((map) => ({
          label: map.name,
          icon: <MoveMapIcon />,
          action: () => moveLayersToMap([layer.id], map.id),
        })),
    });
  }

  return actions;
}
